import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SaveBar } from "@/components/admin/ScheduleTab";

const HERO_TEXTS = [
  ["hero_tagline", "Hero tagline", 0],
  ["hero_story", "Hero story (one paragraph per line)", 10],
  ["hero_quote", "Hero closing quote", 3],
  ["hero_cta", "Hero 'Get Started' button text", 0],
];

const TEXTS = [
  ["quote", "First-page quote (leave both empty to hide it)"],
  ["quote_ref", "Quote reference"],
  ["p2_disclaimer_1", "Page 2 disclaimer 1"],
  ["p2_disclaimer_2", "Page 2 disclaimer 2"],
  ["consent", "Consent text"],
  ["p3_disclaimer_1", "Page 3 disclaimer 1"],
  ["p3_disclaimer_2", "Page 3 disclaimer 2 ({email} becomes the email link)"],
  ["finish_hint", "Final-page hint under 'Finish booking' (shown until the WhatsApp message is sent)"],
  ["final_message", "Final message ({name} becomes the patient's name)"],
  ["reminder", "1-hour reminder to patient ({name} and {time} are filled in)"],
];

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-emerald-100/80">{label}</Label>{children}</div>
);

function Pair({ value, onChange, base, label, rows }) {
  const C = rows ? Textarea : Input;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {["en", "ml"].map((l) => (
        <Field key={l} label={`${label} (${l === "en" ? "English" : "Malayalam"})`}>
          <C data-testid={`field-${base}-${l}`} rows={rows} value={value[`${base}_${l}`] || ""} onChange={(e) => onChange({ ...value, [`${base}_${l}`]: e.target.value })} className="bg-black/20" />
        </Field>
      ))}
    </div>
  );
}

export default function ContentTab({ draft, save }) {
  const [s, setS] = useState(draft);
  const setSvc = (i, patch) => setS({ ...s, services: s.services.map((x, k) => (k === i ? { ...x, ...patch } : x)) });

  return (
    <div className="space-y-8" data-testid="admin-content">
      <section className="space-y-3 rounded-2xl border border-amber-200/25 bg-amber-200/[0.04] p-4" data-testid="admin-hero-texts">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-display text-lg text-white">Hero page (1st page)</p>
          <span className="text-xs text-amber-100/70">Tagline · story · closing quote · "Get Started" button</span>
        </div>
        {HERO_TEXTS.map(([base, label, rows]) => (
          <Pair key={base} value={s.texts} onChange={(v) => setS({ ...s, texts: v })} base={base} label={label} rows={rows || undefined} />
        ))}
      </section>
      <section className="space-y-3">
        <p className="font-display text-lg text-white">Contact & payment</p>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="WhatsApp number (with country code, digits only)"><Input data-testid="field-whatsapp" value={s.whatsapp_number} onChange={(e) => setS({ ...s, whatsapp_number: e.target.value.replace(/\D/g, "") })} className="bg-black/20" /></Field>
          <Field label="Support email"><Input data-testid="field-email" value={s.contact_email} onChange={(e) => setS({ ...s, contact_email: e.target.value })} className="bg-black/20" /></Field>
          <Field label="UPI ID (e.g. name@okaxis)"><Input data-testid="field-upi-id" value={s.upi_id} onChange={(e) => setS({ ...s, upi_id: e.target.value.trim() })} className="bg-black/20" /></Field>
          <Field label="UPI payee name"><Input data-testid="field-upi-name" value={s.upi_name} onChange={(e) => setS({ ...s, upi_name: e.target.value })} className="bg-black/20" /></Field>
          <Field label="WhatsApp first line"><Input data-testid="field-wa-prefill" value={s.texts.whatsapp_prefill} onChange={(e) => setS({ ...s, texts: { ...s.texts, whatsapp_prefill: e.target.value } })} className="bg-black/20" /></Field>
        </div>
      </section>
      <section className="space-y-3">
        <p className="font-display text-lg text-white">Fees & types of help</p>
        {s.services.map((svc, i) => (
          <div key={svc.id} className="space-y-3 rounded-2xl border border-white/10 p-4" data-testid={`service-edit-${svc.id}`}>
            <Pair value={svc} onChange={(v) => setSvc(i, v)} base="label" label="Name" />
            <Pair value={svc} onChange={(v) => setSvc(i, v)} base="duration" label="Duration label" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Price (Rs)"><Input data-testid={`field-price-${svc.id}`} type="number" value={svc.price} onChange={(e) => setSvc(i, { price: Number(e.target.value) })} className="bg-black/20" /></Field>
              <Field label="Minutes blocked in calendar"><Input data-testid={`field-minutes-${svc.id}`} type="number" value={svc.minutes} onChange={(e) => setSvc(i, { minutes: Number(e.target.value) })} className="bg-black/20" /></Field>
            </div>
          </div>
        ))}
      </section>
      <section className="space-y-3">
        <p className="font-display text-lg text-white">Doctor details</p>
        <Pair value={s.doctor} onChange={(v) => setS({ ...s, doctor: v })} base="name" label="Name" />
        <Pair value={s.doctor} onChange={(v) => setS({ ...s, doctor: v })} base="title" label="Qualification" />
        <Pair value={s.doctor} onChange={(v) => setS({ ...s, doctor: v })} base="bio" label="Profile" rows={12} />
      </section>
      <section className="space-y-3 rounded-2xl border border-amber-200/25 bg-amber-200/[0.04] p-4" data-testid="admin-legal-advisor">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-display text-lg text-white">Official Legal Advisor</p>
          <span className="text-xs text-amber-100/70">Shown on 1st page under "Know your doctor" and the forum link</span>
        </div>
        <Pair value={s.legal_advisor || {}} onChange={(v) => setS({ ...s, legal_advisor: v })} base="name" label="Name" />
        <Pair value={s.legal_advisor || {}} onChange={(v) => setS({ ...s, legal_advisor: v })} base="title" label="Qualification / role" />
        <Pair value={s.legal_advisor || {}} onChange={(v) => setS({ ...s, legal_advisor: v })} base="bio" label="Profile" rows={10} />
      </section>
      <section className="space-y-3">
        <p className="font-display text-lg text-white">Page texts</p>
        {TEXTS.map(([base, label]) => (
          <Pair key={base} value={s.texts} onChange={(v) => setS({ ...s, texts: v })} base={base} label={label} rows={base === "final_message" ? 10 : 3} />
        ))}
      </section>
      <SaveBar onSave={() => save(s)} testId="save-content-btn" />
    </div>
  );
}
